import type { FindingSeverity, Urgency } from './model';

/**
 * Wanneer een gekozen antwoord een bevinding is, en hoe urgent: exact de regel
 * van de API (`SeverityRule` in `smartkeur-api`), zodat de tablet dezelfde
 * gebreken en aandachtspunten telt als het rapport.
 *
 * - **gebrek**: oordeel *Actie noodzakelijk* (`1`) of *Onvoldoende* (`2`), of
 *   directe kosten;
 * - **aandachtspunt**: *Matig* (`4`) of *Nader onderzoek* (`-4`), of kosten op
 *   termijn of voor verbetering;
 * - anders geen bevinding, ook bij *Niet waarneembaar*.
 *
 * De urgentie volgt eerst het geld (direct, op termijn, verbetering) en daarna
 * het oordeel.
 */

/** Bedragen in hele centen, net als in de engine. */
export interface CostCents {
    direct: number;
    term: number;
    improvement: number;
}

export const ZERO_COSTS: CostCents = { direct: 0, term: 0, improvement: 0 };

const DEFECT_RESULTS: ReadonlySet<number> = new Set([1, 2]);
const ATTENTION_RESULTS: ReadonlySet<number> = new Set([4, -4]);

export function severityOf(result: number | null, costs: CostCents): FindingSeverity {
    if (costs.direct > 0 || (result !== null && DEFECT_RESULTS.has(result))) {
        return 'defect';
    }

    if (
        costs.term > 0 ||
        costs.improvement > 0 ||
        (result !== null && ATTENTION_RESULTS.has(result))
    ) {
        return 'attention';
    }

    return 'clean';
}

export function urgencyOf(result: number | null, costs: CostCents): Urgency | null {
    if (costs.direct > 0) {
        return 'direct';
    }

    if (costs.term > 0) {
        return 'term';
    }

    if (costs.improvement > 0) {
        return 'improvement';
    }

    if (result !== null && DEFECT_RESULTS.has(result)) {
        return 'direct';
    }

    if (result !== null && ATTENTION_RESULTS.has(result)) {
        return 'term';
    }

    return null;
}

const RANK: Record<FindingSeverity, number> = { clean: 0, attention: 1, defect: 2 };

export function worstSeverity(a: FindingSeverity, b: FindingSeverity): FindingSeverity {
    return RANK[b] > RANK[a] ? b : a;
}

export function addCosts(a: CostCents, b: CostCents): CostCents {
    return {
        direct: a.direct + b.direct,
        term: a.term + b.term,
        improvement: a.improvement + b.improvement,
    };
}

export function totalCents(costs: CostCents): number {
    return costs.direct + costs.term + costs.improvement;
}

export function isZero(costs: CostCents): boolean {
    return costs.direct === 0 && costs.term === 0 && costs.improvement === 0;
}

/**
 * De urgentie van een handmatige bevinding: eerst het geld, dan de soort - net
 * als bij een afgeleide bevinding, waar een gebrek zonder bedrag direct is en
 * een aandachtspunt op termijn.
 */
export function manualUrgency(severity: 'defect' | 'attention', costs: CostCents): Urgency {
    if (costs.direct > 0) {
        return 'direct';
    }

    if (costs.term > 0) {
        return 'term';
    }

    if (costs.improvement > 0) {
        return 'improvement';
    }

    return severity === 'defect' ? 'direct' : 'term';
}
